import type { Phase } from "../../domain/src/types.js";
/**
 * The four moments the interface offers.
 *
 * This is interface vocabulary rather than a rule of the game, which is why it
 * sits beside the store instead of in the domain: it decides how the six
 * underlying situations are presented, not what they are.
 *
 * Underneath there are two independent things — which phase of the rally, and
 * which team is serving — but asking a beginner to combine them is asking them
 * to do arithmetic before they can look at the court. Named as moments, the
 * same six situations read as one list:
 *
 *   base     your rotational slot, before anyone shifts
 *   receive  they serve, and we are taking it
 *   serve    we serve
 *   switch   the ball has been struck and everyone is free
 *
 * Base and switch keep whichever side was serving, so the rotations where the
 * libero steps off to let the middle serve are still reachable both ways.
 */

export type Moment = "base" | "receive" | "serve" | "switch";
export const MOMENTS: Moment[] = ["base", "receive", "serve", "switch"];

/** Which moment a given phase and serve state add up to. */
export function momentOf({ phase, weServe }: { phase: Phase; weServe: boolean }): Moment {
  if (phase === "base") return "base";
  if (phase === "switch" || phase === "defence") return "switch";
  return weServe ? "serve" : "receive";
}

/** The state change a moment asks for, given where we are now. */
export function stateForMoment(moment: Moment, current: { weServe: boolean }): { phase: Phase; weServe: boolean } {
  switch (moment) {
    case "base":
      return { phase: "base", weServe: current.weServe };
    case "receive":
      return { phase: "serve", weServe: false };
    case "serve":
      return { phase: "serve", weServe: true };
    case "switch":
      return { phase: "switch", weServe: current.weServe };
    default:
      throw new Error(`unknown moment: ${moment}`);
  }
}

/** UI Defence uses its own formation; switch remains the reference attack transition. */
export function stateForPresentationMoment(moment: Moment, current: { weServe: boolean }): { phase: Phase; weServe: boolean } {
  return moment === "switch" ? { phase: "defence", weServe: current.weServe } : stateForMoment(moment, current);
}
